export class Dialog {
    dialogTriggers: NodeListOf<Element>;
    dialogClosers: NodeListOf<Element>;
    dialogs: NodeListOf<Element>;
    dialogWidth: string;
    dialogTitle: string;
    dialogClose: boolean;
    initializeValidation: boolean;

    constructor() {
        this.dialogTriggers = document.querySelectorAll("[rel*='dialog:open']");
        this.dialogClosers = document.querySelectorAll("[rel*='dialog:close']");
        this.dialogs = document.querySelectorAll("dialog");
        this.dialogWidth = "";
        this.dialogTitle = "";
        this.dialogClose = true;
        this.initializeValidation = false;
        this.initializeDialog();
    }

    initializeDialog() {
        this.bindEvents();
    }

    bindEvents() {
        document.addEventListener("click", this.handleDocumentClick.bind(this));
    }

    handleDocumentClick(e: MouseEvent) {
        const target = e.target as HTMLElement;
        const openTrigger = target.closest("[rel*='dialog:open']");
        const closeTrigger = target.closest("[rel*='dialog:close']");
        const dialogElement = target.closest("dialog");

        if (openTrigger) {
            this.dialogWidth = openTrigger.getAttribute("data-dialog-width") ?? "";
            this.dialogTitle = openTrigger.getAttribute("data-dialog-title") ?? "";
            this.initializeValidation = openTrigger.getAttribute("data-dialog-validation") === "true";
            this.dialogClose = openTrigger.getAttribute("data-dialog-close") !== "false";
            this.handleTriggerClickWrapper(e, openTrigger);
        } else if (closeTrigger) {
            this.handleCloseClick(e);
        } else if (dialogElement) {
            this.handleOverlayClick(e);
        }
    }

    createDialog(): HTMLDialogElement {
        const dialog = document.createElement("dialog");
        dialog.classList.add("dialog", `--${this.dialogWidth}`);
        document.body.appendChild(dialog);
        return dialog;
    }

    handleTriggerClickWrapper(e: Event, element: Element) {
        e.preventDefault();
        this.handleTriggerClick(e, element).catch(console.error);
    }

    async handleTriggerClick(e: Event, element: Element) {
        e.preventDefault();
        const trigger = element as HTMLElement;
        const type = trigger.getAttribute("data-type") ?? "dialog";
        const dialogId = trigger.getAttribute("href");

        if (dialogId?.startsWith("#")) {
            this.showDialog(dialogId, type);
        } else if (dialogId) {
            await this.loadAndShowDialog(dialogId);
        }
    }

    showDialog(dialogId: string, type: string) {
        const dialog = document.querySelector(dialogId) as HTMLDialogElement;
        if (dialog) {
            if (type === "dialog") dialog.showModal();
            if (type === "modal") dialog.show();
            this.dispatchEvent(dialog, "DIALOG_OPEN");
        }
    }

    async loadAndShowDialog(dialogId: string) {
        const existingDialog = document.querySelector(".initialized-dialog");
        if (existingDialog) existingDialog.remove();

        const newDialog = this.createDialog();
        newDialog.classList.add("initialized-dialog");

        this.populateDialogContent(newDialog, `<p class="mx-0 my-6 text-18 text-center">Loading...</p>`);
        newDialog.showModal();
        this.dispatchEvent(newDialog, "DIALOG_LOADING");

        try {
            const response = await fetch(dialogId, { method: "GET", cache: "no-store" });
            const html = await response.text();
            this.populateDialogContent(newDialog, html);
            this.dispatchEvent(newDialog, "DIALOG_LOADED");
            this.bindCloseEvent(newDialog);
        } catch (error) {
            console.error("Failed to load dialog content", error);
            this.dispatchEvent(newDialog, "DIALOG_ERROR");
            this.populateDialogContent(
                newDialog,
                `<p class="mx-0 my-6 text-18">Failed to load content. Please try again later.</p>`
            );
        }

        newDialog.addEventListener("close", () => newDialog.remove());
    }

    handleCloseClick(e: Event) {
        e.preventDefault();
        const targetElement = e.target as HTMLElement;
        const dialog = this.getClosestDialog(targetElement);
        if (dialog) {
            dialog.close();
            this.dispatchEvent(dialog, "DIALOG_CLOSE");
        }
    }

    handleOverlayClick(e: Event) {
        if (!this.dialogClose) return;
        const target = e.target as HTMLElement;
        const dialog = target.closest("dialog") as HTMLDialogElement;
        if (dialog && target === dialog && dialog.dataset.dialogClose !== "false") {
            dialog.close();
            this.dispatchEvent(dialog, "DIALOG_CLOSE");
        }
    }

    getClosestDialog(element: HTMLElement): HTMLDialogElement | null {
        return element.closest("dialog") as HTMLDialogElement;
    }

    populateDialogContent(dialog: HTMLDialogElement, html: string) {
        this.dialogTitle =
            (this.dialogTitle ||
                new DOMParser().parseFromString(html, "text/html").querySelector(".dialog-title")?.textContent) ??
            "";
        const content = `
            <header>
                <h3>${this.dialogTitle}</h3>
                ${
                    this.dialogClose
                        ? `<button
    type="button"
    title="Close dialog"
    rel="dialog:close nofollow">
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="var(--color-orange-500, currentColor)"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round">
        <title>Close dialog button</title>
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
</button>`
                        : ""
                }
            </header>
            <section class="dialog-content">
                ${html}
            </section>
        `;
        $(dialog).html(content);
        const form = dialog.querySelector("form");
        const submitButton = form?.querySelector("[type='submit']");
        if (form && this.initializeValidation) {
            if (!submitButton) return;
            window.InitializeFormValidation(form.getAttribute("id") as string);
            form.addEventListener("submit", function (event) {
                if (!$(form).valid()) {
                    event.preventDefault();
                    submitButton?.setAttribute("disabled", "disabled");
                } else {
                    submitButton?.classList.add("is-loading");
                }
                form.addEventListener("input", function () {
                    if ($(form).valid()) {
                        submitButton?.removeAttribute("disabled");
                    } else {
                        submitButton?.setAttribute("disabled", "disabled");
                    }
                });
            });
        }
    }

    bindCloseEvent(dialog: HTMLElement) {
        dialog.querySelectorAll<HTMLElement>("[rel*='dialog:close']").forEach((closer) => {
            closer.addEventListener("click", (e) => this.handleCloseClick(e));
        });
        dialog.addEventListener("click", (e) => this.handleOverlayClick(e));
    }

    dispatchEvent(dialog: HTMLDialogElement, eventName: string) {
        const event = new CustomEvent(eventName, { detail: { dialog } });
        dialog.dispatchEvent(event);
    }
}

document.addEventListener("DOMContentLoaded", () => {
    try {
        new Dialog();
    } catch (e) {
        console.log("Failed to initialize dialog", e);
    }
});
